import {
  FileExcelOutlined,
  FilePdfOutlined,
  FileSearchOutlined,
  FileSyncOutlined,
  SearchOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  DatePicker,
  Input,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
} from "antd";
import dayjs from "dayjs";
import { useContext, useState } from "react";
import { ContentBox } from "../../components/layout/content";
import { LoginedLayout } from "../../components/layout/loginLayout";
import { UserContext } from "../../context/userContext";
import { ReserveStockData } from "../../Data/reserveStock";
import { exportToExcel } from "../../functions/exportExcel";
import { GET, PATCH } from "../../services";
import {
  CHECKER_CONFIRM_ORDER,
  CHECKER_GET_RESERVE_STOCK,
  CHECKER_UPDATE_SHIPPING,
} from "../../services/path";
import "./style.css";
import ModalUploadFile from "./view/uploadFile";

const { Option } = Select;
const ReserveStock = () => {
  const [data, setData] = useState<ReserveStockData[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25); // Initial page size
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [isLoading, setLoading] = useState(false); // Initialize loading as true to fetch data initially
  const [isLoadingConfirm, setLoadingConfirm] = useState(false); // Initialize loading as true to fetch data initially
  const [isLoadingShipping, setLoadingShipping] = useState(false); // Initialize loading as true to fetch data initially
  const [isModalVisibleUpload, setIsModalVisibleUpload] = useState(false); // Initialize loading as true to fetch data
  const { user } = useContext(UserContext);
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [searchParams, setSearchParams] = useState({
    searchTextSKU: "",
  });

  const handleSearchInputChange = (field: string, value: string) => {
    setSearchParams({ ...searchParams, [field]: value });
    setCurrentPage(1);
  };

  const filteredData = data.filter((item) => {
    const checkMatch = (fieldValue: string, searchValue: string) => {
      if (!searchValue) return true; // No filter applied if searchValue is empty

      // Split the searchValue into an array if it contains multiple values
      const searchValues = searchValue.split(", ").map((val: string) => val.trim());
      return searchValues.some((val) => fieldValue.toLowerCase().includes(val.toLowerCase()));
    };

    return checkMatch(item.sku ?? "", searchParams.searchTextSKU);
  });

  const columns = [
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            SKU{" "}
          </span>
          <Input
            placeholder="Search Order No."
            value={searchParams.searchTextSKU}
            onChange={(e) => handleSearchInputChange("searchTextSKU", e.target.value)}
            onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
            style={{ width: 200 }}
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "sku",
      key: "sku",
      sorter: (a: ReserveStockData, b: ReserveStockData) => {
        const A = a.sku ?? "";
        const B = b.sku ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "start" }}>
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: <div style={{ textAlign: "center" }}>Physical Item</div>,
      dataIndex: "physical",
      key: "physical",
      sorter: (a: ReserveStockData, b: ReserveStockData) => {
        const A = a.physical ?? "";
        const B = b.physical ?? "";
        return A - B;
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: <div style={{ textAlign: "center" }}>On Order</div>,
      dataIndex: "onorder",
      key: "onorder",
      sorter: (a: ReserveStockData, b: ReserveStockData) => {
        const A = a.onorder ?? "";
        const B = b.onorder ?? "";
        return A - B;
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: <div style={{ textAlign: "center" }}>Available Item</div>,
      dataIndex: "avaliordered",
      key: "avaliordered",
      sorter: (a: ReserveStockData, b: ReserveStockData) => {
        const A = a.avaliordered ?? "";
        const B = b.avaliordered ?? "";
        return A - B;
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: <div style={{ textAlign: "center" }}>ยอดที่สั่งซื้อ online</div>,
      dataIndex: "total_reserve",
      key: "total_reserve",
      sorter: (a: ReserveStockData, b: ReserveStockData) => {
        const A = a.total_reserve ?? "";
        const B = b.total_reserve ?? "";
        return A - B;
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: <div style={{ textAlign: "center" }}>Shipping</div>,
      dataIndex: "shipping",
      key: "shipping",
      sorter: (a: ReserveStockData, b: ReserveStockData) => {
        const A = a.shipping ?? "";
        const B = b.shipping ?? "";
        return A - B;
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: <div style={{ textAlign: "center" }}>ต้องเบิก</div>,
      dataIndex: "must_withdraw",
      key: "must_withdraw",
      sorter: (a: ReserveStockData, b: ReserveStockData) => {
        const A = a.must_withdraw ?? "";
        const B = b.must_withdraw ?? "";
        return A - B;
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span>{text}</span>
        </div>
      ),
    },
  ];

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const pageOptions = ["2", "5", "10", "25", "50", "100"];
  const handleConfirmOrder = async () => {
    try {
      setLoadingConfirm(true);
      if (selectedWarehouse) {
        await PATCH(CHECKER_CONFIRM_ORDER(selectedWarehouse), {});
        message.success("The order has been confirmed successfully.");
      } else {
        message.error("Please select a valid warehouse to confirm the order.");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("An error occurred while confirming the order. Please try again.");
    } finally {
      setLoadingConfirm(false);
    }
  };

  const handleCheckStock = async () => {
    try {
      setLoading(true);
      if (
        selectedWarehouse.trim() === "" ||
        selectedDate === null ||
        selectedLocation.trim() === "" ||
        selectedLocation === null
      ) {
        message.error("Please select a valid warehouse to confirm the order.");
        return;
      }

      const date = selectedDate ? selectedDate.toString() : "";
      const apiEndpoint = CHECKER_GET_RESERVE_STOCK(selectedWarehouse, date, selectedLocation);
      const result = await GET(apiEndpoint);
      setData(result);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateShipping = async () => {
    try {
      setLoadingShipping(true);
      const response = await GET(CHECKER_UPDATE_SHIPPING);
      if (response) {
        message.success('The order has been updated "shipping" successfully.');
      }
    } catch (error) {
      message.error("An error occurred while shipping the order. Please try again.");
    } finally {
      setLoadingShipping(false);
    }
  };

  const handleExportToExcel = () => {
    if (data.length === 0) {
      message.warning("No data to export.");
      return;
    }

    const filterData = data.filter((item) => item.shipping !== null);
    if (filterData.length === 0) {
      message.warning("No data with shipping information to export.");
      return;
    }

    exportToExcel(filterData, "ReserveStockData");
    message.success("Data exported to Excel successfully.");
  };

  const handleOpenModalUpload = () => {
    setIsModalVisibleUpload(true);
  };

  return (
    <LoginedLayout>
      <ContentBox>
        <div
          style={{ marginBottom: "16px", borderBottom: "1px solid #e8e8e8", paddingBottom: "8px" }}
        >
          <h2 style={{ marginBottom: "8px" }}>Reserve Stock</h2>
        </div>
        <Row justify="center" align="middle">
          <Col style={{ marginRight: "15px", marginLeft: "15px" }}>
            <span>Select Date: </span>
            <DatePicker
              style={{ width: "15vw" }}
              onChange={(date) => setSelectedDate(date)}
              value={selectedDate}
            />
          </Col>
          <Col>
            <span>Location :</span>
          </Col>
          <Col style={{ marginRight: "15px", marginLeft: "15px" }}>
            <Select
              placeholder="Select Location"
              style={{ width: "15vw" }}
              onChange={(value) => setSelectedLocation(value)}
              value={selectedLocation}
            >
              <Option value="ONLINE">Online</Option>
              <Option value="ECOM">Ecom</Option>
              <Option value="ALL">All</Option>
            </Select>
          </Col>
          <Col>
            <span>Brand :</span>
          </Col>
          <Col style={{ marginRight: "15px", marginLeft: "15px" }}>
            <Select
              placeholder="Select Warehouse"
              style={{ width: "15vw" }}
              onChange={(value) => setSelectedWarehouse(value)}
              value={selectedWarehouse}
            >
              <Option value="MMT_FOCUS">Focus</Option>
            </Select>
          </Col>
          <Col style={{ margin: "15px" }}>
            <Button
              type="primary"
              loading={isLoading}
              onClick={handleCheckStock}
              icon={<FileSearchOutlined />}
            >
              CheckStock
            </Button>
          </Col>
        </Row>
        <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
          <Col>
            <span>Show: </span>
            <Select
              value={pageSize.toString()}
              onChange={(value) => handlePageSizeChange(parseInt(value))}
            >
              {pageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
          </Col>
          <Col>
            {(user.role_id === 1 || user.role_id === 6 || user.role_id === 7) && (
              <Space>
                <Popconfirm
                  title="confirm order"
                  description="ยืนยัน confirm order ไหม !?"
                  okText="Yes"
                  cancelText="No"
                  onConfirm={handleConfirmOrder}
                >
                  <Button
                    type="primary"
                    loading={isLoadingConfirm}
                    icon={<FileSyncOutlined />}
                    style={{ backgroundColor: "#2196F3", borderColor: "#2196F3", color: "white" }} // Blue color
                  >
                    Confirm
                  </Button>
                </Popconfirm>
                <Popconfirm
                  title="shipping order"
                  description="ยืนยันอัพเดท shipping order ไหม !?"
                  okText="Yes"
                  cancelText="No"
                  onConfirm={handleUpdateShipping}
                >
                  <Button
                    type="primary"
                    loading={isLoadingShipping}
                    icon={<ShoppingCartOutlined />}
                    style={{ backgroundColor: "#F4D03F", borderColor: "#F4D03F", color: "white" }} // Blue color
                  >
                    Shipping
                  </Button>
                </Popconfirm>

                <Button
                  onClick={handleExportToExcel}
                  type="primary"
                  icon={<FileExcelOutlined />}
                  style={{ backgroundColor: "#8BC34A", borderColor: "#8BC34A", color: "white" }} // Lighter green
                >
                  Export Excel
                </Button>
                <Button
                  onClick={handleOpenModalUpload}
                  type="primary"
                  icon={<FilePdfOutlined />}
                  style={{ backgroundColor: "#FF0000", borderColor: "#FF0000", color: "white" }} // Lighter red
                >
                  Upload ใบปะหน้า
                </Button>
              </Space>
            )}
          </Col>
        </Row>
        <Table
          columns={columns}
          dataSource={filteredData}
          loading={isLoading}
          scroll={{ x: 1500 }}
          pagination={{
            position: ["bottomRight"],
            pageSize: pageSize,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            onShowSizeChange: (_, size) => handlePageSizeChange(size),
            current: currentPage,
            onChange: (page) => setCurrentPage(page),
            total: data.length,
          }}
        />
        {isModalVisibleUpload && (
          <ModalUploadFile
            onClose={() => setIsModalVisibleUpload(false)}
            visible={isModalVisibleUpload}
          />
        )}
      </ContentBox>
    </LoginedLayout>
  );
};

export default ReserveStock;
